import React, { useState, useEffect } from 'react';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    DocumentTextIcon,
    ShieldCheckIcon,
    ClipboardDocumentCheckIcon,
    DocumentDuplicateIcon,
    ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ConsentUpload = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        consentGiven: false,
    });
    const [consentDocument, setConsentDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [eligibilityData, setEligibilityData] = useState(null);
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);

    useEffect(() => {
        // Check user eligibility status
        const checkEligibilityStatus = async () => {
            try {
                const response = await axios.get('/profile/general');
                const profileData = response.data;
                
                // Check if user is registered with Athena
                if (!profileData.athena?.isRegistered) {
                    navigate('/profile');
                    return;
                }

                // Check if user has insurance package ID
                if (!profileData.athena?.insurancePackageId) {
                    setError({
                        type: 'address_verification',
                        message: 'Please verify your address to continue. Address verification is required to determine your insurance coverage.'
                    });
                    setIsCheckingAccess(false);
                    return;
                }

                // Check if user has completed eligibility check
                if (!profileData.athena?.eligibility?.isEligibilityChecked) {
                    setError({
                        type: 'eligibility_required',
                        message: 'Please complete your Medicare eligibility check before uploading consent forms.'
                    });
                    setIsCheckingAccess(false);
                    return;
                }

                // Check if user is eligible
                if (!profileData.athena?.eligibility?.isEligible) {
                    setError({
                        type: 'not_eligible',
                        message: 'You are not eligible for Medicare-covered services. Please contact our office for alternative payment options.'
                    });
                    setIsCheckingAccess(false);
                    return;
                }

                setEligibilityData(profileData.athena.eligibility);
                setIsCheckingAccess(false);
            } catch (err) {
                console.error('Error checking eligibility status:', err);
                setError({
                    type: 'error',
                    message: 'Failed to load eligibility status. Please try again.'
                });
                setIsCheckingAccess(false);
            }
        };

        checkEligibilityStatus();
    }, [navigate]);

    const handleInputChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleConsentUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setConsentDocument(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('consentGiven', formData.consentGiven);
            if (consentDocument) {
                formDataToSend.append('consentDocument', consentDocument);
            }

            const response = await axios.post('/athena/customer/consent', formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } catch (err) {
            setError({
                type: 'error',
                message: err.response?.data?.message || 'An error occurred while uploading consent. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading while checking access
    if (isCheckingAccess) {
        return (
            <div className="min-h-screen mb-12 bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // Show error messages
    if (error) {
        return (
            <div className="min-h-screen mb-12 bg-gradient-to-b from-sky-50 via-white to-sky-50">
                <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                    <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                                <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">
                                {error.type === 'address_verification' && 'Address Verification Required'}
                                {error.type === 'eligibility_required' && 'Eligibility Check Required'}
                                {error.type === 'not_eligible' && 'Not Eligible'}
                                {error.type === 'error' && 'Error'}
                            </h1>
                            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                                {error.message}
                            </p>
                            <div className="space-y-4">
                                {error.type === 'address_verification' && (
                                    <button
                                        onClick={() => navigate('/profile')}
                                        className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                    >
                                        Go to Profile to Verify Address
                                    </button>
                                )}
                                {error.type === 'eligibility_required' && (
                                    <button
                                        onClick={() => navigate('/eligibility')}
                                        className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                    >
                                        Check Eligibility
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate('/')}
                                    className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                    Back to Home
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen mb-12 bg-gradient-to-b from-sky-50 via-white to-sky-50">
            {/* Header Section */}
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                            Consent for Services
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Complete your consent forms to receive Medicare-covered home therapy services
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="mt-8">
                    <div className="bg-white rounded-2xl shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                        {/* Eligibility Status */}
                        {eligibilityData && (
                            <div className="mb-8 bg-green-50 rounded-xl p-6">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <CheckCircleIcon className="h-6 w-6 text-green-400" />
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-green-800">
                                            Eligibility Confirmed
                                        </h3>
                                        <div className="mt-2 text-sm text-green-700">
                                            <p>You are eligible for Medicare-covered home therapy services.</p>
                                            {eligibilityData.notes && (
                                                <p className="mt-1 italic">{eligibilityData.notes}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Information Section */}
                        <div className="mb-8 bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-6">
                            <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                                <InformationCircleIcon className="h-6 w-6 mr-2 text-blue-500" />
                                About Consent Forms
                            </h2>
                            <div className="space-y-3 text-sm text-blue-700">
                                <p>
                                    Before we can provide you with home therapy services, we need your consent to:
                                </p>
                                <ul className="list-disc list-inside space-y-1 ml-4">
                                    <li>Receive psychotherapy services in your home</li>
                                    <li>Share your health information with Medicare for billing purposes</li>
                                    <li>Contact you regarding your care and appointments</li>
                                    <li>Coordinate care with your other healthcare providers</li>
                                </ul>
                                <p className="mt-4 text-xs text-blue-600">
                                    Your consent is required by law and helps ensure you receive the best possible care.
                                </p>
                            </div>
                        </div>

                        {/* Success Message */}
                        {success && (
                            <div className="mb-8 bg-green-50 rounded-xl p-6">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <CheckCircleIcon className="h-6 w-6 text-green-400" />
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-green-800">
                                            Consent Submitted Successfully
                                        </h3>
                                        <div className="mt-2 text-sm text-green-700">
                                            <p>Thank you for completing your consent forms. We'll contact you soon to schedule your first appointment.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Consent Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="bg-gray-50 rounded-xl p-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                    <ClipboardDocumentCheckIcon className="h-6 w-6 mr-2 text-gray-500" />
                                    Consent for Services
                                </h2>
                                <div className="space-y-6">
                                    <div className="flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id="consentGiven"
                                                name="consentGiven"
                                                type="checkbox"
                                                required
                                                checked={formData.consentGiven}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor="consentGiven" className="font-medium text-gray-700">
                                                I consent to receive psychotherapy services in my home
                                            </label>
                                            <p className="text-gray-500 mt-1">
                                                By checking this box, you acknowledge that you have read and understand the consent information above.
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <label htmlFor="consentDocument" className="block text-sm font-medium text-gray-700">
                                            Upload Signed Consent Form (PDF)
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                type="file"
                                                name="consentDocument"
                                                id="consentDocument"
                                                accept=".pdf"
                                                onChange={handleConsentUpload}
                                                className="block w-full text-sm text-gray-500
                                                    file:mr-4 file:py-2 file:px-4
                                                    file:rounded-lg file:border-0
                                                    file:text-sm file:font-semibold
                                                    file:bg-blue-50 file:text-blue-700
                                                    hover:file:bg-blue-100
                                                    transition-colors duration-200"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Please upload a signed copy of your consent form. If you don't have one, we can provide a template.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-between items-center">
                                <button
                                    type="button"
                                    onClick={() => navigate('/eligibility')}
                                    className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                >
                                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                    Back to Eligibility
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading || !formData.consentGiven || !consentDocument}
                                    className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Consent'
                                    )}
                                </button>
                            </div>

                            {/* Error Message */}
                            {error && error.type === 'error' && (
                                <div className="rounded-xl p-6 bg-red-50">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <ExclamationCircleIcon className="h-6 w-6 text-red-400" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800">
                                                Error
                                            </h3>
                                            <div className="mt-2 text-sm text-red-700">
                                                <p>{error.message}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentUpload; 